import React, { useEffect } from 'react';

interface SEOHeadProps {
    title: string;
    description: string;
    keywords?: string[];
    canonical?: string;
    ogImage?: string;
    ogType?: 'website' | 'article';
    noindex?: boolean;
    schema?: object[];
}

export const SEOHead: React.FC<SEOHeadProps> = ({
    title,
    description,
    keywords = [],
    canonical,
    ogImage = 'https://rooms.indigobeans.co.za/og-image.jpg',
    ogType = 'website',
    noindex = false,
    schema = []
}) => {
    useEffect(() => {
        // Update document title
        document.title = title;

        // Update or create meta tags
        const updateMetaTag = (name: string, content: string, property?: boolean) => {
            const attribute = property ? 'property' : 'name';
            let element = document.querySelector(`meta[${attribute}="${name}"]`);

            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }

            element.setAttribute('content', content);
        };

        // Basic Meta Tags
        updateMetaTag('description', description);
        if (keywords.length > 0) {
            updateMetaTag('keywords', keywords.join(', '));
        }

        if (noindex) {
            updateMetaTag('robots', 'noindex, nofollow');
        } else {
            updateMetaTag('robots', 'index, follow');
        }

        // Open Graph Tags
        updateMetaTag('og:title', title, true);
        updateMetaTag('og:description', description, true);
        updateMetaTag('og:type', ogType, true);
        updateMetaTag('og:image', ogImage, true);

        if (canonical) {
            updateMetaTag('og:url', canonical, true);
        }

        // Twitter Card Tags
        updateMetaTag('twitter:card', 'summary_large_image');
        updateMetaTag('twitter:title', title);
        updateMetaTag('twitter:description', description);
        updateMetaTag('twitter:image', ogImage);

        // Canonical URL
        if (canonical) {
            let linkElement = document.querySelector('link[rel="canonical"]');
            if (!linkElement) {
                linkElement = document.createElement('link');
                linkElement.setAttribute('rel', 'canonical');
                document.head.appendChild(linkElement);
            }
            linkElement.setAttribute('href', canonical);
        }

        // Structured Data (JSON-LD)
        if (schema.length > 0) {
            // Remove existing schema scripts
            const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
            existingSchemas.forEach(s => s.remove());

            // Add new schema scripts
            schema.forEach((schemaObj, index) => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.text = JSON.stringify(schemaObj);
                script.id = `structured-data-${index}`;
                document.head.appendChild(script);
            });
        }
    }, [title, description, keywords, canonical, ogImage, ogType, noindex, schema]);

    return null; // This component doesn't render anything
};
